import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs,
  updateDoc, 
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase';

export const createBabyBook = async (userId, babyBookData) => {
  try {
    const babyBookRef = doc(collection(db, 'babyBooks'));
    const babyBookId = babyBookRef.id;
    
const babyBook = {
      ...babyBookData,
      id: babyBookId,
      ownerId: userId,
      members: {
        [userId]: 'admin'
      },
      isPremium: false,
      photoCount: 0,
      subscriptionId: null,
      subscriptionStatus: 'free',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    await setDoc(babyBookRef, babyBook);
    
    return { success: true, babyBookId, babyBook };
  } catch (error) {
    console.error('Create baby book error:', error);
    return { success: false, error: error.message };
  }
};

export const getUserBabyBooks = async (userId) => {
  try {
    // Query for books where user is admin
    const adminQuery = query(
      collection(db, 'babyBooks'),
      where(`members.${userId}`, '==', 'admin')
    );
    
    // Query for books where user is editor
    const editorQuery = query(
      collection(db, 'babyBooks'),
      where(`members.${userId}`, '==', 'editor')
    );
    
    // Query for books where user is viewer
    const viewerQuery = query(
      collection(db, 'babyBooks'),
      where(`members.${userId}`, '==', 'viewer')
    );
    
    const [adminSnap, editorSnap, viewerSnap] = await Promise.all([
      getDocs(adminQuery),
      getDocs(editorQuery),
      getDocs(viewerQuery)
    ]);
    
    const babyBooks = [];
    const seenIds = new Set();
    
    [adminSnap, editorSnap, viewerSnap].forEach(snapshot => {
      snapshot.forEach((doc) => {
        if (!seenIds.has(doc.id)) {
          const bookData = doc.data();
          if (!bookData.deleted) {
            babyBooks.push({ id: doc.id, ...bookData });
            seenIds.add(doc.id);
          }
        }
      });
    });
    
    return { success: true, babyBooks };
  } catch (error) {
    console.error('Get baby books error:', error);
    return { success: false, error: error.message };
  }
};

export const getBabyBook = async (babyBookId) => {
  try {
    const babyBookDoc = await getDoc(doc(db, 'babyBooks', babyBookId));
    
    if (!babyBookDoc.exists()) {
      return { success: false, error: 'Baby book not found' };
    }
    
    return { success: true, babyBook: { id: babyBookDoc.id, ...babyBookDoc.data() } };
  } catch (error) {
    console.error('Get baby book error:', error);
    return { success: false, error: error.message };
  }
};

export const updateBabyBook = async (babyBookId, updates) => {
  try {
    await updateDoc(doc(db, 'babyBooks', babyBookId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Update baby book error:', error);
    return { success: false, error: error.message };
  }
};

export const deleteBabyBook = async (babyBookId) => {
  try {
    await updateDoc(doc(db, 'babyBooks', babyBookId), {
      deleted: true,
      deletedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Delete baby book error:', error);
    return { success: false, error: error.message };
  }
};

export const removeUserFromBabyBook = async (babyBookId, userId) => {
  try {
    const babyBookRef = doc(db, 'babyBooks', babyBookId);
    await updateDoc(babyBookRef, {
      [`members.${userId}`]: deleteField()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Remove user error:', error);
    return { success: false, error: error.message };
  }
};

export const updateUserRole = async (babyBookId, userId, newRole) => {
  try {
    const babyBookRef = doc(db, 'babyBooks', babyBookId);
    await updateDoc(babyBookRef, {
      [`members.${userId}`]: newRole
    });
    
    return { success: true };
  } catch (error) {
    console.error('Update user role error:', error);
    return { success: false, error: error.message };
  }
};

// Invite user to baby book
export const inviteUserToBabyBook = async (babyBookId, email, role, invitedByUid) => {
  try {
    // Create invitation document
    const invitationRef = doc(collection(db, 'invitations'));
    const invitationData = {
      id: invitationRef.id,
      babyBookId,
      email: email.toLowerCase(),
      role,
      invitedBy: invitedByUid,
      status: 'pending',
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    };
    
    await setDoc(invitationRef, invitationData);
    
    // Send email notification via Cloud Function
    try {
      const babyBookResult = await getBabyBook(babyBookId);
      const babyBookName = babyBookResult.success ? babyBookResult.babyBook.babyName : 'a baby book';
      
      console.log('About to send email to:', email, 'for baby book:', babyBookName);
      
      await fetch('https://us-central1-babybook-70058.cloudfunctions.net/sendInvitationEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invitedEmail: email,
          role: role,
          babyBookName: babyBookName
        })
      });
      
      console.log('Email sent successfully');
    } catch (error) {
      console.error('Email send failed:', error);
      console.error('Error details:', error.message, error.stack);
    }
    
    return { success: true, invitationId: invitationRef.id };
  } catch (error) {
    console.error('Invite user error:', error);
    return { success: false, error: error.message };
  }
};

// Get pending invitations for a user
export const getUserInvitations = async (email) => {
  try {
    const invitationsRef = collection(db, 'invitations');
    const q = query(
      invitationsRef,
      where('email', '==', email.toLowerCase()),
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(q);
    const invitations = [];
    
    for (const docSnap of snapshot.docs) {
      const invitation = docSnap.data();
      // Get baby book details
      const babyBookSnap = await getDoc(doc(db, 'babyBooks', invitation.babyBookId));
      if (babyBookSnap.exists()) {
        invitation.babyBookName = babyBookSnap.data().babyName;
      }
      invitations.push(invitation);
    }
    
    return { success: true, invitations };
  } catch (error) {
    console.error('Get invitations error:', error);
    return { success: false, error: error.message };
  }
};

// Accept invitation
export const acceptInvitation = async (invitationId, userId) => {
  try {
    const invitationRef = doc(db, 'invitations', invitationId);
    const invitationSnap = await getDoc(invitationRef);
    
    if (!invitationSnap.exists()) {
      return { success: false, error: 'Invitation not found' };
    }
    
    const invitation = invitationSnap.data();
    
    // Add user to baby book members
    const babyBookRef = doc(db, 'babyBooks', invitation.babyBookId);
    await updateDoc(babyBookRef, {
      [`members.${userId}`]: invitation.role
    });
    
    // Mark invitation as accepted
    await updateDoc(invitationRef, {
      status: 'accepted',
      acceptedAt: new Date().toISOString(),
      acceptedBy: userId
    });
    
    return { success: true, babyBookId: invitation.babyBookId };
  } catch (error) {
    console.error('Accept invitation error:', error);
    return { success: false, error: error.message };
  }
};
// Get user's photo count
export const getUserPhotoCount = async (babyBookId) => {
  try {
    const entriesRef = collection(db, 'entries');
    const q = query(entriesRef, where('babyBookId', '==', babyBookId));
    const snapshot = await getDocs(q);
    
    let photoCount = 0;
    snapshot.forEach((doc) => {
      const entry = doc.data();
      if (entry.photos && Array.isArray(entry.photos)) {
        photoCount += entry.photos.length;
      }
    });
    
    return { success: true, count: photoCount };
  } catch (error) {
    console.error('Get photo count error:', error);
    return { success: false, error: error.message };
  }
};
// ============================================
// USER PROFILE FUNCTIONS
// ============================================

// Create user profile when they first sign up
export const createUserProfile = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userProfile = {
      uid: userId,
      email: userData.email || '',
      displayName: userData.displayName || '',
      isPremium: false,
      subscriptionTier: 'free',
      bookLimit: 1,
      photoLimit: 50,
      contributorLimit: 1, // Can invite 1 person (2 total including owner)
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(userRef, userProfile);
    return { success: true, userProfile };
  } catch (error) {
    console.error('Create user profile error:', error);
    return { success: false, error: error.message };
  }
};

// Get user profile
export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      // Profile doesn't exist, create a default one
      console.log('User profile not found, creating default profile');
      return await createUserProfile(userId, { email: '', displayName: '' });
    }
    
    return { success: true, userProfile: { id: userSnap.id, ...userSnap.data() } };
  } catch (error) {
    console.error('Get user profile error:', error);
    return { success: false, error: error.message };
  }
};

// Update user profile
export const updateUserProfile = async (userId, updates) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Update user profile error:', error);
    return { success: false, error: error.message };
  }
};

// Upgrade user to premium
export const upgradeUserToPremium = async (userId, additionalBooks = 0) => {
  try {
    const bookLimit = 3 + additionalBooks; // Base 3 books + any additional
    
    await updateUserProfile(userId, {
      isPremium: true,
      subscriptionTier: 'premium',
      bookLimit: bookLimit,
      photoLimit: -1, // -1 means unlimited
      contributorLimit: 5 // Can invite 5 people per book (6 total including owner)
    });
    
    return { success: true };
  } catch (error) {
    console.error('Upgrade to premium error:', error);
    return { success: false, error: error.message };
  }
};

// Downgrade user to free (triggered when subscription ends)
export const downgradeUserToFree = async (userId) => {
  try {
    await updateUserProfile(userId, {
      isPremium: false,
      subscriptionTier: 'free',
      bookLimit: 1,
      photoLimit: 50,
      contributorLimit: 1
    });
    
    return { success: true };
  } catch (error) {
    console.error('Downgrade to free error:', error);
    return { success: false, error: error.message };
  }
};